import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, ModuleWithProviders, NgModule, Optional, SkipSelf } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { UIModule } from "@app/shared/ui/ui.module";
import { FroalaEditorModule } from "angular-froala-wysiwyg";
import { OAuthModule } from "angular-oauth2-oidc";
import { CLEARLINE_API_CONFIG_TOKEN, ClearlineApiModule } from "clearline-api";
import { CurrentAccountService, ROUTE_PERMISSIONS_MAP, UserPermissionService } from "clearline-common";
import { AccountService as AccountServiceCore, AuthBaseService, CoreKitConfig, CoreKitModule } from "core-kit";
import { environment } from "../../environments/environment";
import { LoadingService } from "./components/spinner";
import { AccountService } from "./services/account.service";
import { AppConfigService } from "./services/app-config.service";
import { AuthGuard } from "./services/auth-guard";
import { AuthService } from "./services/auth.service";
import { DataService } from "./services/data.service";
import { EmailVerifiedGuard } from "./services/email-confirm-guard";
import { EmailVerifyGuard } from "./services/email-verify.guard";
import { GlobalErrorHandler } from "./services/global-error.service";
import { AuthInterceptor } from "./services/interceptors";
import { LandingGuard } from "./services/landing.guard";
import { LogPublishersService, LogService } from "./services/logging";
import { PendingRegistrationGuard } from "./services/pending-registration.guard";
import { routePermissionsMap } from "./services/permission";
import { QuickSetupIntegrationService } from "./services/quick-setup-integration.service";
import { TranslocoRootModule } from "./services/transloco/transloco-root.module";
import { UtilityService } from "./services/utility.service";

export function appConfigServiceFactory(appService: AppConfigService): () => Promise<any> {
  return () => appService.getAppData();
}

const coreKitConfig: CoreKitConfig = {
  apiUrl: environment.apiUrl,
  isProduction: environment.production,
  authConfig: <IAuthConfig>environment.authConfig,
  redirectUrlKey: "adminUIUrl"
};

@NgModule({
  imports: [
    CommonModule,
    OAuthModule.forRoot(),
    FroalaEditorModule.forRoot(),
    HttpClientModule,
    TranslocoRootModule,
    RouterModule,
    CoreKitModule.forRoot(coreKitConfig),
    ClearlineApiModule,
    FormsModule,
    ReactiveFormsModule,
    UIModule
  ],
  exports: [
    RouterModule,
    TranslocoRootModule
    // Components
  ],
  providers: []
})
export class CoreModule {
  // forRoot allows to override providers
  // https://angular.io/docs/ts/latest/guide/ngmodule.html#!#core-for-root
  public static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        AuthService,
        AccountService,
        AuthGuard,
        CurrentAccountService,
        EmailVerifiedGuard,
        DataService,
        LogService,
        LogPublishersService,
        UtilityService,
        LoadingService,
        AppConfigService,
        PendingRegistrationGuard,
        EmailVerifyGuard,
        QuickSetupIntegrationService,
        LandingGuard,
        UserPermissionService,
        { provide: AuthBaseService, useExisting: AuthService },
        { provide: AccountServiceCore, useExisting: AccountService },
        { provide: APP_INITIALIZER, useFactory: appConfigServiceFactory, deps: [AppConfigService], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        {
          provide: CLEARLINE_API_CONFIG_TOKEN,
          useFactory: (appConfigService: AppConfigService) => ({
            apiUrl: appConfigService.apiUrl
          }),
          deps: [AppConfigService]
        },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: ROUTE_PERMISSIONS_MAP, useValue: routePermissionsMap }
      ]
    };
  }
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error("CoreModule is already loaded. Import it in the AppModule only");
    }
  }
}
